/** The root element id `resources/js/app.js` boots Inertia with. */
const ROOT_ID = 'app'

/**
 * Drop the page Inertia saved to `window.history.state` when it predates the
 * deploy currently being served. A no-op on the SSR server.
 *
 * Inertia stores each visited page's props in the browser's history entry so a
 * back/forward navigation can restore them without a round trip. Its popstate
 * handler compares the saved asset version against the running one and reloads
 * on a mismatch, but the path that runs when back/forward triggers a *full*
 * page load — `InitialVisit.handleBackForward()` in `@inertiajs/core`, still
 * true of 3.6.1 — restores the saved page with no version check at all.
 *
 * The result is the newly deployed components rendering against pre-deploy
 * props: every prop a deploy adds is `undefined` on such a visit, which is how
 * `relatedGuides` reached `components/GuideFaq.vue` missing. Clearing the entry
 * before `createInertiaApp()` boots sends Inertia down its default path, which
 * uses the props the server just sent. The saved scroll position goes with it —
 * the right trade for one stale entry, and only on the visits that are broken
 * today.
 */
export function discardStaleHistoryState(): void {
    if (typeof window === 'undefined') {
        return
    }

    const storedPage: unknown = window.history.state?.page

    // Encrypted history is an ArrayBuffer whose version cannot be read here;
    // leave those entries to Inertia's own decrypt path.
    if (
        storedPage === null ||
        typeof storedPage !== 'object' ||
        storedPage instanceof ArrayBuffer
    ) {
        return
    }

    const storedVersion = (storedPage as { version?: unknown }).version
    const servedVersion = servedPageVersion()

    // A null version on both sides means asset versioning is switched off and
    // staleness is not knowable — the only safe move is to leave the entry be.
    if (servedVersion === undefined || storedVersion === servedVersion) {
        return
    }

    window.history.replaceState(null, '')
}

/**
 * The asset version of the page payload the server rendered into this document,
 * read from the same script element `@inertiajs/core` reads it from.
 */
function servedPageVersion(): unknown {
    const payload = document.querySelector(
        `script[data-page="${ROOT_ID}"][type="application/json"]`,
    )

    if (!payload?.textContent) {
        return undefined
    }

    try {
        return (JSON.parse(payload.textContent) as { version?: unknown }).version
    } catch {
        return undefined
    }
}
